import React from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Redirect } from 'react-router-dom';
import publicIp from 'public-ip';

import { createWorkerForm, ICreateWorkerFormData } from 'forms/worker';
import { useTitle } from 'hooks/UseTitle';
import { CreateWorkerProfilePicture } from 'components/CreateWorker/CreateWorkerProfilePicture';
import { CreateWorkerWorkerDetails } from 'components/CreateWorker/CreateWorkerWorkerDetails';
import { CreateWorkerAddress } from 'components/CreateWorker/CreateWorkerAddress';
import { CreateWorkerNextOfKin } from 'components/CreateWorker/CreateWorkerNextOfKin';
import { CreateWorkerTerms } from 'components/CreateWorker/CreateWorkerTerms';
import { clientIp, individualProfilePicture, individualState } from 'state/individual/individual-state';
import { createNewAccount, ICreateNewAccount } from 'modules/create-profile';
import { UpdateProfileImageModal } from 'components/modals/UpdateProfileImageModal/UpdateProfileImageModal';
import { uploadProfileImage } from 'modules/upload-profile-image';
import { ErrorModal } from 'components/modals/ErrorModal/ErrorModal';
// import { CreateWorkerPassword } from 'components/CreateWorker/CreateWorkerPassword';
import { Spinner } from 'components/shared/Spinner/Spinner';

import './styles.scss';

export const CreateWorker = () => {
    const {t} = useTranslation();
    useTitle(t('common.individualRegistration', 'Individual Registration'));
    const {handleSubmit, register, errors, control, reset, setValue} = useForm<ICreateWorkerFormData>({
        resolver: zodResolver(createWorkerForm),
        defaultValues: {
            firstName: '',
            middleName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            phone: '',
            gender: null, // null
            dob: null,
            photo: '',
            address: {
                street: '',
                country: 'AU',
                state: '',
                postCode: '',
                town: ''
            },
            nextOfKin: {
                nokFirstName: '',
                nokLastName: '',
                nokPhoneNumber: '',
                nokRelationship: null, // null
                nokEmail: ''
            },
            // password: {
            //     password: '',
            //     verifyPassword: ''
            // }
        }
    });

    const [token, setToken] = React.useState<string>('');
    const [photoToken, setPhotoToken] = React.useState<string>('');
    const [newWorker, setNewWorker] = useRecoilState(individualState);
    const [clientIP, setClientIP] = useRecoilState(clientIp);
    const profilePicture = useRecoilValue(individualProfilePicture);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [showImageModal, setShowImageModal] = React.useState<boolean>(false);
    const [showFailedModal, setShowFailedModal] = React.useState<boolean | string>(false);

    /**
     * Gets client IP for use in checkout
     */
    React.useEffect(() => {
        if (!clientIP) {
            const getClientIp = async () => await publicIp.v4({
                fallbackUrls: ['https://ifconfig.co/ip']
            });
            getClientIp().then((res) => {
                setClientIP(res);
            });
        }
    }, [clientIP, setClientIP]);

    /**
     * sets recaptcha token when loaded
     */
    const handleLoaded = () => {
        const recaptchaId = (window as any).ENV.REACT_APP_GRECAPTCHA || '';
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(recaptchaId, {action: 'register'})
                .then(token => {
                    setToken(token);
                });
        });
    };

    /**
     * sets recaptcha token when loaded
     */
    const handleLoaded2 = () => {
        const recaptchaId = (window as any).ENV.REACT_APP_GRECAPTCHA || '';
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(recaptchaId, {action: 'register'})
                .then(token => {
                    setPhotoToken(token);
                });
        });
    };

    /**
     * Adds two recaptcha tokens to the page
     * TODO: recaptcha code duplicated here and CredidCardPayment.tsx, clean up
     */
    React.useEffect(() => {
        // Add reCaptcha
        const recaptchaId = (window as any).ENV.REACT_APP_GRECAPTCHA || '';
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaId}`;
        script.addEventListener('load', handleLoaded);
        document.body.appendChild(script);
        const photoScript = document.createElement('script');
        photoScript.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaId}`;
        photoScript.addEventListener('load', handleLoaded2);
        document.body.appendChild(photoScript);
    }, []);

    /**
     * Closes Error modal, if the error is due to recaptcha invalid, refreshes page (loads new tokens)
     */
    const handleCloseModal = () => {
        if (showFailedModal === 'RECAPTCHA') {
            loadRecaptcha();
        }
        setShowImageModal(false);
        setShowFailedModal(false);
    };

    /**
     * Submits create worker form. Calls createNewAccount API and UploadProfileImage API.
     * Displays error modal if needed
     *
     * @param formData raw form data
     */
    const onSubmit = (formData: ICreateWorkerFormData) => {
        setLoading(true);
        (async () => {
            try {
                const response: ICreateNewAccount = await createNewAccount(formData, token);
                // If call is successful and returns a worker id number
                if (response.errorCode && response.message) {
                    setShowFailedModal(response.errorCode);
                } else {
                    setNewWorker({
                        id: response?.workerId ? +response?.workerId : 0,
                        onsiteToken: response?.onsiteToken,
                        ...formData
                    });
                    reset();
                    if (profilePicture) {
                        await uploadProfileImage(
                            profilePicture,
                            response?.workerId ? +response?.workerId : 0,
                            photoToken
                        );
                    }
                }
            } catch (e) {
                alert(e.message);
            }
            setLoading(false);
        })();
    };

    /**
     * reloads recaptcha token. called on user interaction with form elements
     */
    const loadRecaptcha = () => {
        handleLoaded();
        handleLoaded2();
    };

    /**
     * displays the image upload modal
     * @param show
     */
    const handleSetShowImageModal = (show: boolean) => {
        setShowImageModal(show);
        loadRecaptcha();
    };

    // If the worker ID has been set redirect
    if (newWorker?.id) {
        return (
            <Redirect to={`/payment?portal=76&company=${(window as any).ENV.REACT_APP_COMPANY_ID}&employee=${newWorker?.id}`}/>
        );
    }

    return (
        <>
            {/* This ternary conditional ensures the modal is unmounted when not in use */}
            {showImageModal && (
                <UpdateProfileImageModal
                    onClose={handleCloseModal}
                    show={showImageModal}
                    loadRecaptcha={loadRecaptcha}
                />
            )}
            {showFailedModal && (
                <ErrorModal handleClose={handleCloseModal} show={!!showFailedModal} error={showFailedModal}/>
            )}
            <Form onSubmit={handleSubmit(onSubmit)} noValidate className="CreateWorker__form">
                <CreateWorkerProfilePicture
                    register={register}
                    errors={errors}
                    disabled={loading}
                    setShowImageModal={handleSetShowImageModal}
                />
                <CreateWorkerWorkerDetails
                    register={register}
                    errors={errors}
                    control={control}
                    disabled={loading}
                    loadRecaptcha={loadRecaptcha}
                />
                <CreateWorkerAddress
                    register={register}
                    errors={errors}
                    control={control}
                    disabled={loading}
                    setValue={setValue}
                    loadRecaptcha={loadRecaptcha}
                />
                <CreateWorkerNextOfKin
                    register={register}
                    errors={errors}
                    control={control}
                    disabled={loading}
                    loadRecaptcha={loadRecaptcha}
                />
                {/*<CreateWorkerPassword*/}
                {/*    register={register}*/}
                {/*    errors={errors}*/}
                {/*    control={control}*/}
                {/*    disabled={loading}*/}
                {/*    loadRecaptcha={loadRecaptcha}*/}
                {/*/>*/}
                <CreateWorkerTerms
                    register={register}
                    errors={errors}
                    disabled={loading}
                    loadRecaptcha={loadRecaptcha}
                />
                <Button
                    type="submit"
                    variant="primary"
                    className="btn-xl mb-3"
                    disabled={loading}
                    block
                >
                    {loading && (
                        <Spinner variant="primary" size="sm"/>
                    )}
                    {!loading && t('createProfile.actions.submitNext', 'Next')}
                </Button>
                <div className="text-center">
                    <a href={(window as any).ENV.REACT_APP_DSP_LOGIN}>
                        {t('createWorker.actions.alreadyRegistered', 'Already Registered? Log in Here')}
                    </a>
                </div>
            </Form>
            <div
                className="g-recaptcha"
                data-sitekey="_reCAPTCHA_site_key_"
                data-size="invisible"
            />
            <div className="footer-margin"/>
        </>
    );
};
